import { getTranslation } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import React from 'react';

import type { CheckoutFormStatus } from '../../hooks/usePaddleCheckout';
import { useWindowDimensions } from '../../hooks/useWindow';
import {
  PADDLE_FRAME_CLASS,
  PADDLE_FRAME_INITIAL_HEIGHT,
} from '../../lib/Billing/paddleInlineFrame';
import { Button } from '../Button.components';
import LoadingSpinner from '../LoadingSpinner.component';

/** Split-pane layout above this width; stacked summary-then-form below it. */
const TWO_PANE_MIN_WIDTH = 960;
/** Width of the content column inside each pane (Paddle's inline form needs at least 312). */
const PANE_CONTENT_WIDTH = 400;
/**
 * Pane gutters on desktop: wide on the divider side so the two content columns sit well apart,
 * narrower on the outer edges. At the breakpoint each pane still leaves 336px for content.
 */
const PANE_OUTER_GUTTER = 40;
const PANE_INNER_GUTTER = 104;

const FormFailed: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <Layout.Column align py={32}>
    <Icon name="alert-circle-outline" size={40} color={theme.colors.destructive} />
    <Spacer.Vertical size={12} />
    <OpenSans.Primary size="m-18" weight="bold-700" center>
      {getTranslation("We couldn't load the payment form")}
    </OpenSans.Primary>
    <Spacer.Vertical size={6} />
    <OpenSans.Secondary size="s-16" center>
      {getTranslation('Check your connection and try again. You have not been charged.')}
    </OpenSans.Secondary>
    <Spacer.Vertical size={20} />
    <Button.Primary variant="bold" onClick={onRetry} content={getTranslation('Try again')} />
    <Spacer.Vertical size={16} />
    <OpenSans.Secondary size="xs-12" center>
      {getTranslation('Still stuck? Email support@easy-expense.com')}
    </OpenSans.Secondary>
  </Layout.Column>
);

/**
 * The shell every checkout route renders into: a split-pane checkout in the Stripe Checkout
 * mould, with the caller's order summary on the left and Paddle's inline form on the right,
 * stacking summary-then-form on a narrow screen.
 *
 * The payment pane is owned here rather than by the page because it is the same on every route --
 * the same mount point, the same spinner while Paddle is coming up, the same recovery when it
 * never does. `summary` is a render prop so the pane can tighten its own spacing when stacked.
 */
export const CheckoutLayout: React.FC<{
  status: CheckoutFormStatus;
  onRetry: () => void;
  /** Summary pane content. `compact` is true in the stacked (narrow) layout. */
  summary: (compact: boolean) => React.ReactNode;
}> = ({ status, onRetry, summary }) => {
  const { width } = useWindowDimensions();
  const stacked = width < TWO_PANE_MIN_WIDTH;
  const paneContent = { width: '100%', maxWidth: PANE_CONTENT_WIDTH } as const;

  const summaryPane = (
    <Layout.Column
      bg="white"
      align={stacked ? undefined : 'flex-end'}
      px={stacked ? 20 : undefined}
      py={stacked ? 24 : 48}
      style={
        stacked
          ? undefined
          : {
              flex: 1,
              minHeight: '100vh',
              paddingLeft: PANE_OUTER_GUTTER,
              paddingRight: PANE_INNER_GUTTER,
            }
      }
    >
      <Layout.Column style={paneContent}>{summary(stacked)}</Layout.Column>
    </Layout.Column>
  );

  const paymentPane = (
    <Layout.Column
      bg="white"
      px={stacked ? 20 : undefined}
      py={stacked ? 24 : 48}
      style={
        stacked
          ? { borderTop: `1px solid ${theme.colors.grayXLight}` }
          : {
              flex: 1,
              minHeight: '100vh',
              paddingLeft: PANE_INNER_GUTTER,
              paddingRight: PANE_OUTER_GUTTER,
              borderLeft: `1px solid ${theme.colors.grayXLight}`,
              boxShadow: '-12px 0 32px rgba(0, 0, 0, 0.05)',
            }
      }
    >
      <Layout.Column style={{ ...paneContent, minHeight: PADDLE_FRAME_INITIAL_HEIGHT }}>
        <OpenSans.Primary size="m-18" weight="bold-700">
          {getTranslation('Payment details')}
        </OpenSans.Primary>
        <Spacer.Vertical size={16} />
        {status === 'failed' ? <FormFailed onRetry={onRetry} /> : null}
        <Layout.Column style={{ display: status === 'failed' ? 'none' : 'flex' }}>
          <div className={PADDLE_FRAME_CLASS} style={{ width: '100%' }} />
          {/* Only before Paddle's frame is up. Once it is ('opening'), it draws its own loading
              indicator inside the iframe, and ours would sit on top of it as a second spinner. */}
          {status === 'loading' ? (
            <Layout.Column
              align
              center
              style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
            >
              <LoadingSpinner size={36} />
              <Spacer.Vertical size={12} />
              <OpenSans.Secondary size="s-16">
                {getTranslation('Loading secure payment form…')}
              </OpenSans.Secondary>
            </Layout.Column>
          ) : null}
        </Layout.Column>
      </Layout.Column>
    </Layout.Column>
  );

  if (stacked) {
    return (
      <Layout.Column bg="white" style={{ minHeight: '100vh', width: '100%' }}>
        {summaryPane}
        {paymentPane}
      </Layout.Column>
    );
  }
  return (
    <Layout.Row align="stretch" bg="white" style={{ minHeight: '100vh', width: '100%' }}>
      {summaryPane}
      {paymentPane}
    </Layout.Row>
  );
};
